<template>
  <div class="category-list-page">
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />

    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="getList"
      :editCategory="selected"
    />

    <v-card :loading="loading" outlined class="category-card">
      <div class="category-header">
        <div>
          <div class="category-title">Category</div>
          <div class="category-subtitle">
            Manage product categories, product count, ordering, and collections.
          </div>
        </div>

        <div class="category-actions">
          <v-btn
            v-if="checkRightStatus(8)"
            small
            rounded
            color="primary"
            dark
            @click="add"
          >
            <v-icon left small>mdi-plus</v-icon>
            New Category
          </v-btn>
        </div>
      </div>

      <div class="category-filter-bar">
        <div class="filter-field search-field">
          <div class="filter-label">Search Category</div>
          <v-text-field
            v-model="search"
            outlined
            prepend-inner-icon="search"
            dense
            label="Search by name or collection"
            single-line
            hide-details
            clearable
            class="category-search"
          />
        </div>

        <v-spacer />

        <v-btn
          small
          rounded
          :color="singleExpand ? 'primary' : ''"
          :outlined="!singleExpand"
          :dark="singleExpand"
          @click="singleExpand = !singleExpand"
        >
          <v-icon left small>
            {{ singleExpand ? "mdi-unfold-less-horizontal" : "mdi-unfold-more-horizontal" }}
          </v-icon>
          Single Expand
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="categorys"
          :search="search"
          :loading="loading"
          :sort-by="sortBy"
          :page.sync="page"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
          show-expand
          :items-per-page="itemsPerPage"
          :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
        >
          <template v-slot:item="{ item, isExpanded, expand }">
            <tr class="pointer">
              <td class="text-left">
                <v-btn @click="expand(!isExpanded)" icon>
                  <v-icon>
                    {{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                  </v-icon>
                </v-btn>
              </td>

              <td class="text-left">
                <div class="category-name-wrap">
                  <ImageIcon :item="item" type="category" />
                  <span>{{ item.name }}</span>
                </div>
              </td>

              <td class="text-left">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-chip
                      small
                      label
                      color="blue lighten-5"
                      text-color="blue darken-3"
                      v-on="on"
                    >
                      <v-icon x-small left>mdi-package-variant-closed</v-icon>
                      {{ item.productCount || 0 }}
                    </v-chip>
                  </template>
                  <span>
                    {{ item.productCount || 0 }} products ·
                    {{ item.productBalance || 0 }} total inventory units
                  </span>
                </v-tooltip>
              </td>

              <td class="text-left">
                <div class="ordering-statuses">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <span
                        class="ordering-status-icon"
                        :class="{ 'is-disabled': !item.remoteOrder }"
                        v-on="on"
                      >
                        <v-icon
                          small
                          :color="item.remoteOrder ? 'green darken-2' : 'grey'"
                        >
                          mdi-web
                        </v-icon>
                      </span>
                    </template>
                    <span>
                      Remote ordering is {{ item.remoteOrder ? "on" : "off" }}
                    </span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <span
                        class="ordering-status-icon"
                        :class="{ 'is-disabled': !item.catalogOrder }"
                        v-on="on"
                      >
                        <v-icon
                          small
                          :color="item.catalogOrder ? 'blue darken-2' : 'grey'"
                        >
                          mdi-book-open-outline
                        </v-icon>
                      </span>
                    </template>
                    <span>
                      Catalog ordering is {{ item.catalogOrder ? "on" : "off" }}
                    </span>
                  </v-tooltip>
                </div>
              </td>

              <td class="text-left">
                <span v-if="item.Collection">{{ item.Collection.name }}</span>
                <span v-else class="grey--text">Not Set</span>
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item
                      v-if="checkRightStatus(24)"
                      @click="editCategory(item)"
                    >
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="checkRightStatus(8)"
                      @click="deleteCategory(item)"
                    >
                      <v-list-item-title>Delete</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <v-card color="#f8fafc" flat tile>
                <v-card-text class="pa-3">
              <PhoneList
                :CategoryId="item.id"
                @products-changed="getList"
              />
                </v-card-text>
              </v-card>
            </td>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import categroyService from "@/modules/Product/Category/service.js";
import PhoneList from "@/modules/Product/components/PhoneList";
import ImageIcon from "@/components/Image/ImageIcon.vue";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const Edit = () => import("./Edit");

export default {
  name: "category-list",

  components: {
    Create,
    PhoneList,
    ImageIcon,
    Edit,
  },

  data() {
    return {
      createDialog: false,
      editDialog: false,
      selected: null,
      expanded: [],
      singleExpand: true,
      headers: [
        { text: "", value: "data-table-expand" },
        {
          text: this.$t("labels.name"),
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Products",
          align: "left",
          value: "productCount",
          sortable: true,
        },
        {
          text: "Ordering",
          align: "left",
          value: "remoteOrder",
          sortable: false,
        },
        {
          text: "Collection",
          align: "left",
          value: "Collection.name",
          sortable: true,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      categorys: [],
      search: "",
      loading: true,
      page: 1,
      sortBy: "name",
      pageCount: 0,
      itemsPerPage: 10,
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
  },

  created() {
    this.getList();
    document.title = "smplsale - Category";
  },

  methods: {
    add() {
      this.createDialog = true;
    },

    getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      return categroyService
        .getAll()
        .then((response) => {
          this.categorys = response.data || [];
          return response;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    editCategory(category) {
      this.selected = category;
      this.editDialog = true;
    },

    async deleteCategory(category) {
      // Refresh first because products can be deleted from the expanded row
      // while this category object still contains its previous count.
      await this.getList();
      const currentCategory = this.categorys.find(item => item.id === category.id);
      if (!currentCategory) return;

      if (currentCategory.systemKey === "uncategorized") {
        await this.$swal({
          title: "Category Required",
          text: "Uncategorized is a protected system category and cannot be deleted.",
          icon: "info",
        });
        return;
      }

      let mode = "moveProducts";
      let confirm = undefined;
      const productCount = Number(currentCategory.productCount) || 0;

      if (productCount > 0) {
        const choice = await this.$swal.fire({
          title: `Delete ${currentCategory.name}?`,
          html: `This category contains <strong>${productCount} product(s)</strong>. Choose what should happen to them.`,
          icon: "warning",
          input: "radio",
          inputOptions: {
            moveProducts: "Move products to Uncategorized",
            deleteProducts: `Delete all ${productCount} products`,
          },
          inputValue: "moveProducts",
          inputValidator: value => !value ? "Please choose an option" : undefined,
          showCancelButton: true,
          confirmButtonText: "Continue",
          cancelButtonText: "Cancel",
        });
        if (!choice.value) return;
        mode = choice.value;

        if (mode === "deleteProducts") {
          const typed = await this.$swal.fire({
            title: "Delete category and products?",
            html: `This will move <strong>${productCount} product(s)</strong> to Deleted Products.<br><br>Type <strong>DELETE ALL</strong> to confirm.`,
            icon: "error",
            input: "text",
            inputAttributes: { autocapitalize: "off", autocomplete: "off" },
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Delete All",
            inputValidator: value => value === "DELETE ALL" ? undefined : 'Type "DELETE ALL" to continue',
          });
          if (typed.value !== "DELETE ALL") return;
          confirm = typed.value;
        }
      } else {
        const confirmation = await this.$swal({
          title: `Delete ${currentCategory.name}?`,
          text: "This category has no products.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "Cancel",
          confirmButtonText: "Delete Category",
        });
        if (!confirmation.value) return;
      }

      try {
        await categroyService.delete(currentCategory.id, { mode, confirm });
        await this.$swal("Deleted!", "Category has been deleted.", "success");
        await this.getList();
      } catch (error) {
        await this.$swal({
          title: "Unable to delete category",
          text: error && error.data && error.data.message
            ? error.data.message
            : "Please try again.",
          icon: "error",
        });
      }
    },
  },
};
</script>

<style scoped>
.category-list-page {
  width: 100%;
}

.category-card {
  border-radius: 20px;
  background: #ffffff;
  overflow: hidden;
}

.category-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.category-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.category-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.category-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.category-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.search-field {
  min-width: 320px;
}

.category-search {
  max-width: 360px;
}

.category-name-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ordering-statuses {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ordering-status-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #f1f5f9;
}

.ordering-status-icon:not(.is-disabled) {
  background: #ecfdf5;
}

.ordering-status-icon.is-disabled::after {
  content: "";
  position: absolute;
  width: 20px;
  height: 2px;
  border-radius: 2px;
  background: #ef4444;
  transform: rotate(-45deg);
}

.pointer:hover {
  background: #f8fafc;
}

@media (max-width: 600px) {
  .category-header {
    align-items: flex-start;
  }

  .category-actions,
  .category-actions .v-btn {
    width: 100%;
  }

  .search-field,
  .category-search {
    min-width: 100%;
    max-width: 100%;
  }

  .category-filter-bar .v-btn {
    width: 100%;
  }
}
</style>
